import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  AuditTrailEvent,
  AuditTrailFilters,
  AuditTrailResponse,
  AuditTrailResponsePagedResults,
  AuditTrailApiResponse,
  AuditTrailStats,
} from '../models/audit-trail.models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root',
})
export class AuditTrailService {
  private http = inject(HttpClient);

  private readonly baseUrl = environment.BASE_URL;

  // Loading states
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Search audit trails using the new API
   */
  searchAuditTrails(
    filters: AuditTrailFilters,
  ): Observable<AuditTrailResponse> {
    this.loadingSubject.next(true);

    let params = new HttpParams();

    // Operation is optional for search endpoint - only include when specified
    if (
      filters.operation &&
      filters.operation !== 'All' &&
      filters.operation.trim() !== ''
    ) {
      params = params.set('operation', filters.operation);
    }

    // Optional parameters
    if (filters.tenantId) {
      params = params.set('tenantId', filters.tenantId.toString());
    }

    if (filters.clientId) {
      params = params.set('clientId', filters.clientId.toString());
    }

    if (filters.entityType && filters.entityType !== 'All') {
      params = params.set('entityType', filters.entityType);
    }

    if (filters.entityId) {
      params = params.set('entityId', filters.entityId);
    }

    if (filters.fields) {
      params = params.set('fields', filters.fields);
    }

    if (filters.fromDate) {
      params = params.set('fromDate', filters.fromDate);
    }

    if (filters.toDate) {
      params = params.set('toDate', filters.toDate);
    }

    params = params.set('pageNumber', (filters.pageNumber || 1).toString());
    params = params.set('pageSize', (filters.pageSize || 20).toString());

    const url =
      this.baseUrl + CONST_URL_SEGEMENTS.AUDIT_TRAIL.SEARCH_AUDIT_TRAILS;

    return this.http.get<AuditTrailResponsePagedResults>(url, { params }).pipe(
      map((response) => this.transformResponse(response, filters)),
      catchError((error) => {
        console.error('Error searching audit trails:', error);
        this.loadingSubject.next(false);
        return of({
          events: [],
          total_items: 0,
          current_page: 1,
          total_pages: 0,
        });
      }),
      map((response) => {
        this.loadingSubject.next(false);
        return response;
      }),
    );
  }

  /**
   * Get audit trails for a specific entity
   */
  getAuditTrails(
    entityType: string,
    entityId: string,
    filters: AuditTrailFilters,
  ): Observable<AuditTrailResponse> {
    this.loadingSubject.next(true);

    let params = new HttpParams()
      .set('pageNumber', (filters.pageNumber || 1).toString())
      .set('pageSize', (filters.pageSize || 20).toString());

    if (filters.tenantId) {
      params = params.set('tenantId', filters.tenantId.toString());
    }

    if (filters.clientId) {
      params = params.set('clientId', filters.clientId.toString());
    }

    if (filters.operation && filters.operation !== 'All') {
      params = params.set('operation', filters.operation);
    }

    if (entityId) {
      params = params.set('entityId', entityId);
    }

    const url =
      this.baseUrl +
      CONST_URL_SEGEMENTS.AUDIT_TRAIL.GET_ENTITY_AUDIT_TRAIL.replace(
        '{entity_type}',
        entityType,
      );

    return this.http.get<AuditTrailResponsePagedResults>(url, { params }).pipe(
      map((response) => this.transformResponse(response, filters)),
      catchError((error) => {
        console.error('Error fetching audit trails:', error);
        this.loadingSubject.next(false);
        return of({
          events: [],
          total_items: 0,
          current_page: 1,
          total_pages: 0,
        });
      }),
      map((response) => {
        this.loadingSubject.next(false);
        return response;
      }),
    );
  }

  /**
   * Get audit trail statistics
   */
  getAuditTrailStats(): Observable<AuditTrailStats> {
    // Mock implementation - replace with actual API call when available
    return of({
      totalEventsToday: 247,
      activeUsers: 18,
      mostActiveEntity: 'Clients',
      errorRate: 0.2,
    });
  }

  /**
   * Export audit trail data
   */
  exportAuditTrails(
    filters: AuditTrailFilters,
    format: 'csv' | 'excel' = 'csv',
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);

    // Add required operation parameter
    if (filters.operation) {
      params = params.set('operation', filters.operation);
    } else {
      params = params.set('operation', 'CREATE,UPDATE,DELETE');
    }

    // Add other filters
    if (filters.entityType) {
      params = params.set('entityType', filters.entityType);
    }

    if (filters.tenantId) {
      params = params.set('tenantId', filters.tenantId.toString());
    }

    return this.http.get(
      this.baseUrl + CONST_URL_SEGEMENTS.AUDIT_TRAIL.EXPORT_AUDIT_TRAILS,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Get available entity types
   */
  getEntityTypes(): string[] {
    return ['All', 'Plan', 'Client', 'Class', 'Staff', 'Payment'];
  }

  /**
   * Get available operations
   */
  getOperations(): string[] {
    return ['All', 'CREATE', 'UPDATE', 'DELETE'];
  }

  /**
   * Get time ago format
   */
  getTimeAgo(timestamp: string): string {
    const now = new Date();
    const eventTime = new Date(timestamp);
    const diffInMinutes = Math.floor(
      (now.getTime() - eventTime.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  }

  /**
   * Transform API response to match our interface
   */
  private transformResponse(
    response: AuditTrailResponsePagedResults,
    filters?: AuditTrailFilters,
  ): AuditTrailResponse {
    let transformedEvents: AuditTrailEvent[] = (response.items || []).map(
      (apiEvent, index) => this.transformApiEventToEvent(apiEvent, index),
    );

    // Apply client-side filtering for staff IDs (since API doesn't support staff filtering)
    if (filters?.selectedStaffIds && filters.selectedStaffIds.length > 0) {
      transformedEvents = transformedEvents.filter(
        (event) =>
          event.staff_id && filters.selectedStaffIds?.includes(event.staff_id),
      );
    }

    // Apply client-side search filtering if provided
    if (filters?.searchTerm && filters.searchTerm.trim()) {
      const searchTerm = filters.searchTerm.toLowerCase();
      transformedEvents = transformedEvents.filter(
        (event) =>
          event.entity_type?.toLowerCase().includes(searchTerm) ||
          event.entity_primary_name?.toLowerCase().includes(searchTerm) ||
          event.operation?.toLowerCase().includes(searchTerm) ||
          event.field?.toLowerCase().includes(searchTerm),
      );
    }

    return {
      events: transformedEvents,
      total_items: response.total_items || 0, // Use filtered count
      current_page: response.current_page || 1,
      total_pages: response.total_pages || 0,
    };
  }

  /**
   * Transform API event to UI event with enhanced data
   */
  private transformApiEventToEvent(
    apiEvent: AuditTrailApiResponse,
    index: number,
  ): AuditTrailEvent {
    // Generate UI ID for tracking
    const id = Date.now() + index;

    // Extract field from deltas or fields property
    let field = '';
    if (apiEvent.deltas && apiEvent.deltas.length > 0) {
      field = apiEvent.deltas[0].field;
    } else if (apiEvent.fields) {
      field = apiEvent.fields;
    }

    // Compute severity based on operation
    let severity: 'low' | 'medium' | 'high' = 'low';
    if (apiEvent.operation === 'DELETE') {
      severity = 'high';
    } else if (apiEvent.operation === 'UPDATE') {
      severity = 'medium';
    }

    // Resolve app source from track_mode
    let app_source = 'Web App';
    if (apiEvent.track_mode === 1) {
      app_source = 'Staff App';
    } else if (apiEvent.track_mode === 2) {
      app_source = 'Admin Panel';
    } else if (apiEvent.track_mode === 3) {
      app_source = 'Mobile App';
    }

    return {
      ...apiEvent,
      id,
      field,
      severity,
      app_source,
    };
  }
}
